import React, { useState, useEffect } from "react";
import { io } from "socket.io-client";
import Table from "./components/Table";
import TableSidebar from "./components/TableSidebar";
import TableCentric from "./components/table/TableCentric";
import TableCasino from "./components/table/casino/TableCasino";
import Lobby from "./components/Lobby";
import { SERVER_URL, API_BASE } from "./config";
import { rejoinTable } from "./api";
import "./styles/lobby.css";


function getStoredPlayer() {
  try {
    return JSON.parse(localStorage.getItem("player")) || null;
  } catch (e) {
    return null;
  }
}

function getStoredTableID() {
  try {
    return localStorage.getItem("tableID") || "";
  } catch (e) {
    return "";
  }
}

function setStoredTableID(tableID) {
  try {
    if (tableID) {
      localStorage.setItem("tableID", tableID);
    } else {
      localStorage.removeItem("tableID");
    }
  } catch (e) {
    // Ignore storage errors
  }
}

function normalizeTableCode(code) {
  return (code || "").trim().toLowerCase().replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
}

function getTableCodeFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("table");
    if (code && code.trim().length > 0) return normalizeTableCode(code);
  } catch (err) {}
  return "";
}


function persistGameSession(tableID, playerObj) {
  try {
    if (playerObj) localStorage.setItem("player", JSON.stringify(playerObj));
    if (tableID) localStorage.setItem("tableID", tableID);
    // Do not persist ?table= in the URL — that triggers invite-only lobby on refresh.
    const params = new URLSearchParams(window.location.search);
    params.delete("table");
    params.delete("spectate");
    params.delete("invite");
    const qs = params.toString();
    window.history.replaceState(
      {},
      document.title,
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
  } catch (e) {
    // Ignore storage errors
  }
}

function clearSession() {
  try {
    localStorage.removeItem("player");
    localStorage.removeItem("tableID");
    // Clear URL parameters and redirect to root
    window.history.replaceState({}, document.title, window.location.pathname);
    return true;
  } catch (e) {
    console.error("Error clearing session:", e);
    return false;
  }
}

function checkForClearSession() {
  try {
    const params = new URLSearchParams(window.location.search);
    const shouldClear = params.get("clear") === "true" || params.get("clear") === "1";
    const hashClear = window.location.hash === "#clear" || window.location.hash === "#reset";
    
    if (shouldClear || hashClear) {
      clearSession();
      // Remove the clear parameter from URL without reloading
      const newUrl = window.location.pathname + (window.location.search.replace(/[?&]clear=(true|1)/, '').replace(/^&/, '?') || '');
      window.history.replaceState({}, document.title, newUrl);
      return true;
    }
  } catch (err) {
    console.error("Error checking for clear session:", err);
  }
  return false;
}

function checkForSidebarMode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    return params.get("sidebar") === "true" || params.get("sidebar") === "1" || path.includes("/sidebar") || path.includes("/prototype");
  } catch (err) {
    return false;
  }
}

function checkForTableCentricMode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    return params.get("tablecentric") === "true" || params.get("tablecentric") === "1" || path.includes("/table-centric") || path.includes("/new-layout");
  } catch (err) {
    return false;
  }
}

/** Casino v2 presentation: default on gutzv2 host, or ?casino=1 / /v2 */
function checkForCasinoMode() {
  try {
    const host = (window.location.hostname || "").toLowerCase();
    if (host.includes("gutzv2")) return true;
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    return (
      params.get("casino") === "1" ||
      params.get("casino") === "true" ||
      params.get("v2") === "1" ||
      path.includes("/v2") ||
      path.includes("/casino")
    );
  } catch (err) {
    return false;
  }
}

function App() {
  // Check for clear session parameter first (before loading stored data)
  const wasCleared = checkForClearSession();
  const useSidebar = checkForSidebarMode();
  const useCasino = checkForCasinoMode();
  const useTableCentric = !useCasino && checkForTableCentricMode();
  
  // Get table code from URL if present (invite link flow)
  const urlTableCode = getTableCodeFromURL();
  const storedTableID = wasCleared ? "" : getStoredTableID();
  const storedPlayer = wasCleared ? null : getStoredPlayer();
  const inviteTargetsDifferentTable = urlTableCode && storedTableID && normalizeTableCode(storedTableID) !== urlTableCode;
  const initialPlayer = inviteTargetsDifferentTable ? null : storedPlayer;
  const initialTableID = urlTableCode || storedTableID || "";
  const [socket, setSocket] = useState(null);
  const [tableID, setTableID] = useState(initialTableID);
  const [player, setPlayer] = useState(initialPlayer);
  const [table, setTable] = useState(null);
  const [error, setError] = useState("");
  const [isRejoining, setIsRejoining] = useState(false);
  const [rejoinKey, setRejoinKey] = useState(0);

  // -------- Socket.io connection --------
  useEffect(() => {
    // Configure Socket.IO with proper transport fallback
    // Socket.IO uses /socket.io/ path by default, which works with NPM proxy
    const s = io(SERVER_URL, { 
      autoConnect: true,
      transports: ['websocket', 'polling'], // Try websocket first, fallback to polling
      upgrade: true, // Allow transport upgrades
      rememberUpgrade: true, // Remember successful transport
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // Force path to /socket.io/ (Socket.IO default, but explicit for clarity)
      path: '/socket.io/',
      // When behind proxy, ensure we use the correct protocol
      forceNew: false,
      // Add debug logging to help diagnose connection issues
      debug: process.env.NODE_ENV === 'development'
    });
    setSocket(s);
    return () => s.disconnect();
  }, []);

  useEffect(() => {
    if (!socket) return;
    function handleUpdate(data) {
      setTable(data.table);
    }
    function handleAnteRequestError(data) {
      setError(data.message);
      // Clear error after 5 seconds
      setTimeout(() => setError(""), 5000);
    }
    socket.on("update", handleUpdate);
    socket.on("anteRequestError", handleAnteRequestError);
    return () => {
      socket.off("update", handleUpdate);
      socket.off("anteRequestError", handleAnteRequestError);
    };
  }, [socket]);

  // Track if we've already joined the table to prevent infinite loop
  const [hasJoinedTable, setHasJoinedTable] = useState(false);
  
  useEffect(() => {
    if (!socket || !tableID || !player || !table || hasJoinedTable) return;
    // Only emit joinTable once when we first have all required data
    console.log('[App] Emitting joinTable:', { tableID, playerID: player.playerID });
    socket.emit("joinTable", { tableID, playerID: player.playerID });
    setHasJoinedTable(true);
  }, [socket, tableID, player, table, hasJoinedTable]);
  
  // Reset hasJoinedTable when tableID or player changes
  useEffect(() => {
    setHasJoinedTable(false);
  }, [tableID, player?.playerID]);

  useEffect(() => {
    if (player) localStorage.setItem("player", JSON.stringify(player));
  }, [player]);

  useEffect(() => {
    if (tableID) setStoredTableID(tableID);
  }, [tableID]);

  // Try to rejoin table on mount if we have both tableID and playerID
  useEffect(() => {
    if (tableID && player && player.playerID && !table) {
      setIsRejoining(true);
      console.log('[App] Attempting to rejoin table:', tableID, player.playerID);
      rejoinTable(tableID, player.playerID)
        .then((data) => {
          console.log('[App] Rejoin successful:', data);
          if (data.table) {
            setTable(data.table);
          } else {
            console.error('[App] Rejoin failed: no table data');
            setError("Could not rejoin table. The table may have ended or the server restarted.");
          }
        })
        .catch((err) => {
          console.error('[App] Rejoin error:', err);
          clearSession();
          setPlayer(null);
          setTableID("");
          setTable(null);
          setError("Could not rejoin table — session cleared. Create or join a new table.");
        })
        .finally(() => setIsRejoining(false));
    }
  }, [tableID, player, table, rejoinKey]);

  // Lobby join/create - now handled by Lobby component

  /** Leave table UI but keep saved session so the lobby can offer rejoin. */
  function handleTakeBreak() {
    setTable(null);
    setPlayer(null);
    setError("");
    setHasJoinedTable(false);
  }

  /** Permanently leave — removes player from table and clears saved session. */
  function handleQuit() {
    if (socket && tableID && player?.playerID) {
      socket.emit("quitTable", { tableID, playerID: player.playerID });
    }
    clearSession();
    setPlayer(null);
    setTable(null);
    setTableID("");
    setError("");
    setHasJoinedTable(false);
  }

  // ------------- LOBBY UI -------------
  if (!player || !player.playerID) {
    return (
      <div className="guts-lobby-page">
        <Lobby
          onJoin={({ tableID, playerID, name, avatarUrl, isSpectator, rejoin }) => {
            const playerObj = { playerID, name, avatarUrl, isSpectator: !!isSpectator };
            persistGameSession(tableID, playerObj);
            setTableID(tableID);
            setPlayer(playerObj);
            if (rejoin) setRejoinKey((k) => k + 1);
          }}
        />
      </div>
    );
  }

  // ------------- TABLE GAME UI -------------
  return (
    <div>
      {error && <div style={{ color: "red", fontWeight: "bold", padding: 8 }}>{error}</div>}
      {table && socket && player ? (
        useCasino ? (
          <TableCasino
            table={table}
            player={player}
            socket={socket}
            onTakeBreak={handleTakeBreak}
            onQuit={handleQuit}
          />
        ) : useTableCentric ? (
          <TableCentric
            table={table}
            player={player}
            socket={socket}
            onTakeBreak={handleTakeBreak}
            onQuit={handleQuit}
          />
        ) : useSidebar ? (
          <TableSidebar
            table={table}
            player={player}
            session={null}
            socket={socket}
            onTakeBreak={handleTakeBreak}
            onQuit={handleQuit}
          />
        ) : (
          <Table
            table={table}
            player={player}
            session={null}
            socket={socket}
            onTakeBreak={handleTakeBreak}
            onQuit={handleQuit}
          />
        )
      ) : (
        <div style={{ textAlign: "center", marginTop: 60, fontSize: 22, padding: 24 }}>
          {isRejoining ? "Rejoining table..." : error ? error : "Joining table..."}
          {error && !isRejoining && tableID && player?.playerID && (
            <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setTable(null);
                  setRejoinKey((k) => k + 1);
                }}
                style={{ padding: "10px 20px", fontSize: 16, cursor: "pointer" }}
              >
                Retry rejoin
              </button>
              <button
                type="button"
                onClick={handleTakeBreak}
                style={{ padding: "10px 20px", fontSize: 16, cursor: "pointer" }}
              >
                Return to lobby
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
